import { useEffect, useState } from "react"
import { Form, Row, Col } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { updateTrip } from "store/trips"
import AddStaffModal from "pagesV2/pro/Staff/AddStaffModal"
import { useResourceData } from "pagesV2/pro/Experiences/NewListingResources/hooks/useResourceData"
import { useEquipmentManagement } from "pagesV2/pro/Experiences/NewListingResources/hooks/useEquipmentManagement"
import { useCrewMemberManagement } from "pagesV2/pro/Experiences/NewListingResources/hooks/useCrewMemberManagement"
import { EquipmentSection } from "pagesV2/pro/Experiences/NewListingResources/components/EquipmentSection"
import { CrewMemberSection } from "pagesV2/pro/Experiences/NewListingResources/components/CrewMemberSection"
import { buildResourcesPayload } from "../mappers/toApiPayload"
import { useWizardContext } from "../WizardContext"

/**
 * Resources step — equipment and crew member assignment.
 *
 * No AntD form fields used here; all state is local and registered
 * via stepSaveHandlersRef so useExperienceWizard can trigger the save.
 */
export const ResourcesStep = () => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const { stepSaveHandlersRef } = useWizardContext()
  const trip = useSelector(modelSelector("trip"))

  const [autoAssignEquipment, setAutoAssignEquipment] = useState(false)
  const { equipmentData, crewMemberData, categoryOptions, roleOptions } = useResourceData()

  const {
    selectedEquipment,
    setSelectedEquipment,
    selectedCategories,
    setSelectedCategories,
    selectedEquipmentData,
    handleEditEquipment,
    handleRemoveEquipment,
    handleOpenModal
  } = useEquipmentManagement(equipmentData)

  const {
    selectedCrewMembers,
    setSelectedCrewMembers,
    selectedCrewMembersData,
    selectedRoles,
    setSelectedRoles,
    isAddStaffModalVisible,
    editingCrewMember,
    handleEditCrewMember,
    handleRemoveCrewMember,
    handleOpenStaffModal,
    handleCloseStaffModal
  } = useCrewMemberManagement(crewMemberData)

  // Initialize from trip server state
  useEffect(() => {
    if (trip?.boat_ids) setSelectedEquipment(trip.boat_ids.map(String))
    if (trip?.assigned_staff_ids) setSelectedCrewMembers(trip.assigned_staff_ids.map(String))
    if (trip?.auto_assign_equipment !== undefined) setAutoAssignEquipment(trip.auto_assign_equipment)
  }, [trip, setSelectedEquipment, setSelectedCrewMembers])

  // Register save handler
  useEffect(() => {
    stepSaveHandlersRef.current["resources"] = async () => {
      const payload = buildResourcesPayload({ selectedEquipment, selectedCrewMembers, autoAssignEquipment })
      await dispatch(updateTrip(id, payload))
    }
    return () => {
      delete stepSaveHandlersRef.current["resources"]
    }
  }, [dispatch, id, stepSaveHandlersRef, selectedEquipment, selectedCrewMembers, autoAssignEquipment])

  return (
    <Form component={false}>
      <Row gutter={[16, 16]} className="mt-4">
        <Col lg={12}>
          <EquipmentSection
            equipmentData={equipmentData}
            selectedEquipment={selectedEquipment}
            onEquipmentChange={setSelectedEquipment}
            selectedCategories={selectedCategories}
            onCategoryChange={setSelectedCategories}
            categoryOptions={categoryOptions}
            selectedEquipmentData={selectedEquipmentData}
            onEdit={handleEditEquipment}
            onRemove={handleRemoveEquipment}
            onAddClick={handleOpenModal}
            autoAssignEquipment={autoAssignEquipment}
            onAutoAssignChange={setAutoAssignEquipment}
          />
        </Col>
        <Col lg={12}>
          <CrewMemberSection
            crewMemberData={crewMemberData}
            selectedCrewMembers={selectedCrewMembers}
            onCrewMemberChange={setSelectedCrewMembers}
            selectedRoles={selectedRoles}
            onRoleChange={setSelectedRoles}
            roleOptions={roleOptions}
            selectedCrewMembersData={selectedCrewMembersData}
            onEdit={handleEditCrewMember}
            onRemove={handleRemoveCrewMember}
            onAddClick={handleOpenStaffModal}
          />
        </Col>
      </Row>
      <AddStaffModal
        staffId={editingCrewMember?.id}
        editMode={!!editingCrewMember?.id}
        open={isAddStaffModalVisible}
        onCancel={handleCloseStaffModal}
        memberType="crew"
      />
    </Form>
  )
}
